import { computed } from "vue";

import useBackendClient from "@/client";
import useToast from "@/hooks/useToastNotification";
import { ENTITY_CONFIG } from "@/pages/games/GameDisplay/children/versionHistory/versionDiff.config";
import type {
  APIVersionDiff,
  DiffSection,
  EntityChange,
} from "@/pages/games/GameDisplay/children/versionHistory/versionDiff.types";

export default function useVersionDiff(gameId: string, baseVersionId?: string | null, targetVersionId?: string | null) {
  const { client, useAxios } = useBackendClient();
  const { toastError } = useToast();

  const requestBody = {
    ...(baseVersionId && { baseVersionId }),
    ...(targetVersionId && { targetVersionId }),
  };

  const { isLoading, isFinished, data, execute } = useAxios<APIVersionDiff>(
    `/app/${gameId}/version/diff`,
    { method: "post", data: requestBody },
    client,
    {
      immediate: true,
      onError: () => {
        toastError({
          detail: "Failed to load version changes. Try again later.",
          summary: "Error",
          life: 3000,
        });
      },
    }
  );

  const sections = computed<DiffSection[]>(() => {
    if (!data.value) {
      return [];
    }

    // Build a flat name lookup from all raw changes
    const nameMap: Record<string, string> = {};
    Object.values(ENTITY_CONFIG).forEach((cfg) => {
      (data.value![cfg.sectionKey] ?? []).forEach((c) => {
        nameMap[c.entityId] = c.displayName;
      });
    });

    return Object.entries(ENTITY_CONFIG)
      .map(([type, cfg]) => ({
        type,
        key: cfg.sectionKey,
        title: cfg.label,
        icon: cfg.icon,
        color: cfg.color,
        changes: (data.value![cfg.sectionKey] ?? []).map((c) => ({
          ...c,
          dependenciesWithChangesByType: Object.fromEntries(
            Object.entries(c.dependenciesWithChangesByType).map(([depType, ids]) => [
              depType,
              ids.map((id) => ({ id, displayName: nameMap[id] ?? id })),
            ])
          ),
        })),
      }))
      .filter((section) => section.changes.length > 0);
  });

  const allChanges = computed<EntityChange[]>(() => sections.value.flatMap((s) => s.changes));

  const diffGeneratedAt = computed(() => data?.value?.versionDiffGeneratedAtMillis);

  const refetch = () => execute();

  return { data, isLoading, isFinished, sections, allChanges, diffGeneratedAt, refetch };
}
